/**
 * Reine Textbausteine der Empfehlungskarte (P1-V3 § 2.2/§ 4), DOM-frei und getestet (`empfehlungText.test.ts`).
 * Die Gründe kommen als feste Satzbausteine aus dem Backend (`webinarPrognose.warumBausteine`); hier bekommen sie
 * ihre Richtung (+ spricht dafür, − dagegen) und die Wortwahl der Kapitelseite.
 */
export interface Grund {
  richtung: '+' | '−' | null;
  text: string;
}

/**
 * Bezug der Pausen: das Backend zählt sie bis zum Zielsonntag (`webinarPrognose`), nicht bis heute. Mit `ziel` und den
 * Pausen in Tagen schreibt `grundText` deshalb Daten statt Abständen („FUM zuletzt am 25.10.") — ein Abstand ab dem
 * Zielsonntag widersprach den Abständen ab heute in der Produkte-Tabelle (Persona-Check 26.10.2026, B15).
 */
export interface PausenBezug {
  ziel: string;
  pause_produkt: number | null;
  pause_konzept: number | null;
  pause_thema: number | null;
}

/** `ziel` minus `tage` als „TT.MM." */
function vorZiel(ziel: string, tage: number): string {
  const t = new Date(`${ziel.slice(0, 10)}T00:00:00Z`);
  t.setUTCDate(t.getUTCDate() - tage);
  const iso = t.toISOString();
  return `${iso.slice(8, 10)}.${iso.slice(5, 7)}.`;
}

/** Wortwahl P1-V3 § 4: „reife Läufe" → „Termine mit fertigen Zahlen", „Konzept" → „dieses Webinar". */
export function grundText(w: string, bezug?: PausenBezug): string {
  if (bezug) {
    const { ziel, pause_produkt: pp, pause_konzept: pk, pause_thema: pt } = bezug;
    const produkt = w.match(/^(.+) lief vor \d+ Tagen$/);
    if (produkt && produkt[1] !== 'Thema' && pp !== null) return `${produkt[1]} zuletzt am ${vorZiel(ziel, pp)}`;
    if (/^Thema lief vor \d+ Tagen$/.test(w) && pt !== null) return `Thema zuletzt am ${vorZiel(ziel, pt)}`;
    if (/^Konzept lief vor \d+ Wochen$/.test(w) && pk !== null) return `dieses Webinar zuletzt am ${vorZiel(ziel, pk)}`;
    if (/^Pause erst \d+ Wochen$/.test(w) && pk !== null) return `lief zuletzt am ${vorZiel(ziel, pk)}`;
    if (/^lange Pause \(\d+ Wochen\)$/.test(w) && pk !== null) return `seit ${vorZiel(ziel, pk)} nicht gelaufen`;
  }
  return w
    .replace(/^noch kein reifer Lauf$/, 'noch kein Termin mit fertigen Zahlen')
    .replace(/^erst (\d+) reifer Lauf$/, 'erst $1 Termin mit fertigen Zahlen')
    .replace(/^erst (\d+) reife Läufe$/, 'erst $1 Termine mit fertigen Zahlen')
    .replace(/^Konzept lief vor 1 Wochen$/, 'dieses Webinar lief vor 1 Woche')
    .replace(/^Konzept lief vor (\d+) Wochen$/, 'dieses Webinar lief vor $1 Wochen')
    .replace(/^lange Pause \((\d+) Wochen\)$/, '$1 Wo. nicht gelaufen')
    .replace(/^Pause erst (\d+) Wochen$/, 'lief erst vor $1 Wochen');
}

const DAFUER = [/^stärker als/, /über dem Schnitt/, /^lange Pause/];
const DAGEGEN = [/^schwächer als/, /unter dem Schnitt/, /^Pause erst/, /^erst \d+ reife/, /^noch kein reifer/];

/** Gründe mit Richtung, höchstens `max`; Anlässe stehen eigens und fallen hier weg. */
export function gruendeMitRichtung(warum: string[], max = 3, bezug?: PausenBezug): Grund[] {
  return warum
    .filter((w) => !w.startsWith('Anlass'))
    .map((w) => ({
      richtung: DAFUER.some((re) => re.test(w)) ? ('+' as const) : DAGEGEN.some((re) => re.test(w)) ? ('−' as const) : null,
      text: grundText(w, bezug),
    }))
    .sort((a, b) => Number(b.richtung === '+') - Number(a.richtung === '+'))
    .slice(0, max);
}

/** Letzter Platz, der mit Platz 1 gleichauf liegt (`gleichwertig` zusammenhängend ab Platz 2); 1 = keiner. */
export function gleichaufBis(kandidaten: { rang: number | null; gleichwertig: boolean }[]): number {
  const nachRang = kandidaten.filter((k) => k.rang !== null).sort((a, b) => (a.rang as number) - (b.rang as number));
  let n = nachRang.length ? 1 : 0;
  for (const k of nachRang.slice(1)) {
    if (!k.gleichwertig) break;
    n = k.rang as number;
  }
  return n;
}

/** `2026-11-15` → „15.11." */
const tm = (d: string) => `${d.slice(8, 10)}.${d.slice(5, 7)}.`;

/**
 * Kopfzeile des offenen Abschnitts „Gesperrt" (P1-V3 § 2.2): Anzahl, wie viele über dem letzten gleichauf liegenden
 * Platz liegen (mit Namen, höchstens drei), und wann wie viele wieder frei sind.
 */
export function gesperrtKopf(
  ziel: string,
  gesperrt: { title: string; plx_product: string | null; median: number | null; frei_ab: string | null }[],
  schwelle: { platz: number; median: number | null } | null,
): string {
  const kopf = `Gesperrt am ${tm(ziel)} (${gesperrt.length})`;
  const teile: string[] = [];
  if (schwelle && schwelle.median !== null) {
    const ueber = gesperrt.filter((k) => k.median !== null && (k.median as number) > (schwelle.median as number));
    if (ueber.length) {
      const namen = ueber.slice(0, 3).map((k) => `${k.title} (${k.plx_product ?? '–'}${k.frei_ab ? `, frei ab ${tm(k.frei_ab)}` : ''})`);
      teile.push(`${ueber.length} davon ${ueber.length === 1 ? 'liegt' : 'liegen'} über Platz ${schwelle.platz}: ${namen.join(', ')}${ueber.length > 3 ? ' …' : ''}`);
    }
  }
  const jeTag = new Map<string, number>();
  for (const k of gesperrt) if (k.frei_ab) jeTag.set(k.frei_ab, (jeTag.get(k.frei_ab) ?? 0) + 1);
  const frei = [...jeTag.entries()].sort((a, b) => a[0].localeCompare(b[0])).map(([d, n]) => `${n} ab ${tm(d)}`);
  if (frei.length) teile.push(`Wieder frei: ${frei.join(', ')}`);
  return teile.length ? `${kopf} — ${teile.join('. ')}` : kopf;
}

/** Wahrscheinlichkeit, dass ein Kandidat besser ist als Platz 1 (Normalnäherung auf der log-Skala, wie das Backend). */
export function chanceBesser(k: { mu?: number | null; v?: number | null }, erster: { mu?: number | null; v?: number | null }): number | null {
  if (k.mu == null || k.v == null || erster.mu == null || erster.v == null) return null;
  const s = Math.sqrt(k.v + erster.v);
  if (!(s > 0)) return null;
  return phi((k.mu - erster.mu) / s);
}

/** Standardnormalverteilung Φ (Abramowitz/Stegun 7.1.26) — dieselbe Näherung wie `webinarPrognose.phi`. */
export function phi(x: number): number {
  const t = 1 / (1 + (0.3275911 * Math.abs(x)) / Math.SQRT2);
  const y =
    1 - ((((1.061405429 * t - 1.453152027) * t + 1.421413741) * t - 0.284496736) * t + 0.254829592) * t * Math.exp(-(x * x) / 2);
  return x >= 0 ? (1 + y) / 2 : (1 - y) / 2;
}

// ─── Ähnlicher Titel (Persona-Check 26.10.2026, B24) ─────────────────────────────────────────────
// Zwei Konzepte mit fast gleichem Titel (EBD „Der Eine-Billion-Dollar-Chip" und BDC „Der 1 Billion Dollar Chip") führt
// das System als verschiedene Webinare; die Pause des einen sagt nichts über das andere. Ob der Inhalt gleich ist,
// entscheidet Marketing — die Karte nennt nur den Hinweis. Verglichen wird der Haupttitel (vor „ - " / „ | ").

const FUELLWOERTER = new Set(['der', 'die', 'das', 'den', 'dem', 'des', 'ein', 'eine', 'einer', 'eines', 'und', 'mit', 'für', 'von', 'zum', 'zur', 'live', 'sondersendung', 'webinar']);

/** Bedeutungstragende Wörter des Haupttitels (klein, ohne Füllwörter, ab 3 Zeichen). */
export function titelWoerter(titel: string): Set<string> {
  const haupt = titel.split(/\s[-–|]\s|\|/).find((t) => t.trim().length > 0) ?? titel;
  return new Set(
    haupt
      .toLowerCase()
      .split(/[^a-zäöüß0-9]+/)
      .filter((w) => w.length >= 3 && !FUELLWOERTER.has(w)),
  );
}

/** Ähnlich: mindestens zwei gemeinsame Wörter und Jaccard ≥ 0,6 auf den Haupttiteln. */
export function titelAehnlich(a: string, b: string): boolean {
  const x = titelWoerter(a);
  const y = titelWoerter(b);
  let gemeinsam = 0;
  x.forEach((w) => {
    if (y.has(w)) gemeinsam += 1;
  });
  const vereinigt = x.size + y.size - gemeinsam;
  return gemeinsam >= 2 && vereinigt > 0 && gemeinsam / vereinigt >= 0.6;
}

/**
 * Jüngster Lauf eines anderen Kandidaten desselben Produkts mit ähnlichem Titel, wenn er jünger ist als der eigene
 * letzte Lauf — sonst null. Ergebnis: Startdatum `YYYY-MM-DD`.
 */
export function aehnlicherLief(
  k: { key: string; plx_product: string | null; title: string; last_run: string },
  alle: { key: string; plx_product: string | null; title: string; last_run: string }[],
): string | null {
  const treffer = alle
    .filter((o) => o.key !== k.key && o.plx_product === k.plx_product && o.last_run > k.last_run && titelAehnlich(o.title, k.title))
    .map((o) => o.last_run.slice(0, 10))
    .sort();
  return treffer.length ? treffer[treffer.length - 1] : null;
}

/** Abstand ab heute in Wochen, sprachlich richtig: „diese Woche", „vor 1 Woche", „vor 3 Wochen" (B15/B32). */
export const vorWochen = (tage: number): string => {
  const w = Math.round(tage / 7);
  return w <= 0 ? 'diese Woche' : w === 1 ? 'vor 1 Woche' : `vor ${w} Wochen`;
};

// ─── Befundzeile der Empfehlung (Persona-Umbau B3, 28.10.2026) ───────────────────────────────────

const WT = ['So', 'Mo', 'Di', 'Mi', 'Do', 'Fr', 'Sa'];
/** `2026-11-08` → „So 08.11." (Wochentag aus dem Kalenderdatum, ohne Zeitzonen-Umrechnung). */
export function sonntagKurz(d: string): string {
  const [j, m, t] = d.slice(0, 10).split('-').map(Number);
  return `${WT[new Date(Date.UTC(j, m - 1, t)).getUTCDay()]} ${String(t).padStart(2, '0')}.${String(m).padStart(2, '0')}.`;
}

/** Haupttitel für den Satz (vor „ | " bzw. „ - "), höchstens `max` Zeichen. */
const satzTitel = (t: string, max = 60): string => {
  const h = (t.split(/\s[-–|]\s|\s\|\s?/)[0] ?? t).trim();
  return h.length > max ? `${h.slice(0, max - 1).trimEnd()}…` : h;
};

/** € in T€ mit einer Stelle („6,7 T€"). */
const tEur = (v: number) => `${(v / 1000).toLocaleString('de-DE', { minimumFractionDigits: 1, maximumFractionDigits: 1 })} T€`;

/**
 * „So 08.11.: Der Absahnplan (KBO) ≈ 6,7 T€ — Plätze 1–3 gleichauf" — aus der Tabelle, für die die Karte gilt (nächster
 * freier Sonntag). Ohne Platz 1: „kein Webinar frei". `produktName` setzt bei Artikelnummern den Namen ein.
 */
export function empfehlungBefund(
  p: { zieltermin: string; kandidaten: { rang: number | null; gleichwertig: boolean; title: string; plx_product: string | null; median: number | null }[] },
  produktName?: (code: string) => string,
): string {
  const tag = sonntagKurz(p.zieltermin);
  const erster = p.kandidaten.find((k) => k.rang === 1);
  if (!erster) return `${tag}: kein Webinar frei — alle Kandidaten sind gesperrt.`;
  const produkt = erster.plx_product ? ` (${produktName ? produktName(erster.plx_product) : erster.plx_product})` : '';
  const wert = erster.median !== null ? ` ≈ ${tEur(erster.median)}` : '';
  const n = gleichaufBis(p.kandidaten);
  const zweiter = p.kandidaten.find((k) => k.rang === 2);
  const zusatz =
    n > 1 ? ` — Plätze 1–${n} gleichauf` : zweiter && zweiter.median !== null ? ` — klar vor Platz 2 (≈ ${tEur(zweiter.median)})` : '';
  return `${tag}: ${satzTitel(erster.title)}${produkt}${wert}${zusatz}`;
}
